// External
import React, { Suspense, lazy, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import 'remixicon/fonts/remixicon.css';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
// import 'bootstrap/dist/css/bootstrap.min.css'; //test
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AppLayout from './Deepecho/AppLayout';

import GlobalContextProvider from './Deepecho/state/GlobalContextProvider';
import { ProtectedPages } from './Deepecho/Pages';
import publicPages from './Deepecho/PublicPages';
import DefaultLayout from './Deepecho/DefaultLayout';
import ProtectedRoutes from './Deepecho/ProtectedRoutes';
import PublicRoutes from './Deepecho/PublicRoutes';

const TwoFactorSetupPage = lazy(() => import(/* webpackChunkName: "2fa-setup" */ './Deepecho/UI/Pages/TwoFactorSetup/TwoFactorSetup.page'));
const SharedReportPage = lazy(() => import(/* webpackChunkName: "shared-report" */ './Deepecho/UI/Pages/SharedReport/shared-report.page'));
const SignupPage = lazy(() => import(/* webpackChunkName: "signup" */ './Deepecho/UI/Pages/Signup/signup.page'));
const LandingPage = lazy(() => import(/* webpackChunkName: "signup" */ './Deepecho/UI/Pages/Signup/Landing'));

import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import NotFoundRedirect from './Deepecho/UI/ComponnentsUI/common/NotFound';
import { CategoriesProvider } from './Deepecho/contexts/CategoriesContext';
import { FilterOptionsProvider } from './Deepecho/contexts/FilterOptionsContext';




// Fills its container, not the viewport, so the shell stays visible.
const PageFallback = () => (
  <div className="flex h-full w-full flex-1 items-center justify-center bg-card-color-200 dark:bg-gray-900">
    <div
      role="status"
      aria-label="Loading page"
      className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400"
    />
  </div>
);

function App() {



  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1,
            // Keep the old payload visible while a refetch runs (e.g. facility
            // switch). Pages that check `isFetching` can render a subtle overlay
            // instead of unmounting into a full-page loader.
            placeholderData: keepPreviousData,
          },
        },
      })
  );





  return (
    <div className="bg-card-color-200">
      <QueryClientProvider client={queryClient}>
        <CategoriesProvider>
          <FilterOptionsProvider>
          <>
            <BrowserRouter basename="/">
              {/* Only for the lazy routes that render outside a layout. */}
              <Suspense fallback={<PageFallback />}>
                <ToastContainer />
                <Routes>

                  {/* Patient exam share. Deliberately bare: no PublicRoutes
                      (it redirects logged-in users to /home, which would stop a
                      clinician opening a link they just created) and no
                      DefaultLayout (the recipient is not a user of this app). */}
                  <Route
                    path="/shared/:token"
                    element={<SharedReportPage />}
                  />

                  {/* Any other shape of /shared/ URL is a mangled link. It has to
                      be caught here, because the "*" route below redirects to
                      /home, which is protected and bounces to /login: a patient
                      with no account would be asked to sign in. The page reads a
                      missing token as an unavailable link and says so. */}
                  <Route path="/shared/*" element={<SharedReportPage />} />

                  {/* Training portal sign-up. Bare like /shared: it has its own
                      shell, and PublicRoutes would send the user to /home the
                      moment the flow signs them in (before 2FA and the plan). */}
                  <Route path="/signup" element={<SignupPage />} />

                  {/* Training tenants introduce DeepEcho here to signed-out
                      visitors; everyone else goes on to /home. */}
                  <Route path="/" element={<LandingPage />} />

                  <Route
                    path="/2fa-setup"
                    element={
                      <PublicRoutes>
                        <TwoFactorSetupPage />
                      </PublicRoutes>
                    }
                  />

                  {ProtectedPages.filter(
                    (page) => page.path !== '/2fa-setup'
                  ).map((page, key) => (
                    <Route
                      key={key}
                      path={page.path}
                      element={
                        <ProtectedRoutes requiredPermissions={page.requiredPermissions}>
                          <GlobalContextProvider>
                            <AppLayout>
                              {/* Inside the layout: keeps the shell mounted. */}
                              <Suspense fallback={<PageFallback />}>
                                {page.element}
                              </Suspense>
                            </AppLayout>
                          </GlobalContextProvider>
                        </ProtectedRoutes>
                      }
                    />
                  ))}

                  {publicPages.map((page, key) => (
                    <Route
                      key={key}
                      path={page.path}
                      element={
                        <PublicRoutes>
                          <DefaultLayout>
                            <Suspense fallback={<PageFallback />}>
                              {page.element}
                            </Suspense>
                          </DefaultLayout>
                        </PublicRoutes>
                      }
                    />
                  ))}
                  <Route path="*" element={<NotFoundRedirect />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </>
          </FilterOptionsProvider>
        </CategoriesProvider>
      </QueryClientProvider>
    </div>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
};

App.defaultProps = {
  config: {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBaseName: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions: [],
};

export default App;
